import { useState, useEffect } from 'react';

const steps = [
  { label: "Génération IA", icon: "sparkle" },
  { label: "Retouche", icon: "pen" },
  { label: "Publication", icon: "upload" },
];

const IllustrationWorkflow = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Progress bar fills over 2.5s, then move to next step
    const tick = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          setActiveStep(s => (s + 1) % steps.length);
          return 0;
        }
        return p + 2;
      });
    }, 50);
    return () => clearInterval(tick);
  }, []);

  const StepIcon = ({ type, active }: { type: string; active: boolean }) => {
    const cls = `w-[14px] h-[14px] transition-colors duration-500 ${active ? 'text-primary/70' : 'text-muted-foreground/30'}`;
    switch (type) {
      case 'sparkle':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}>
            <path d="M12 3v18M3 12h18M5.6 5.6l12.8 12.8M18.4 5.6 5.6 18.4" />
          </svg>
        );
      case 'pen':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}>
            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
          </svg>
        );
      case 'upload':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" x2="12" y1="3" y2="15" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="mt-6 mb-2">
      <div className="rounded-lg border border-primary/20 bg-primary/[0.03] overflow-hidden max-w-[300px] shadow-sm">
        {/* Title bar */}
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-primary/10 bg-primary/[0.04]">
          <span className="w-[7px] h-[7px] rounded-full bg-primary/20" />
          <span className="w-[7px] h-[7px] rounded-full bg-primary/15" />
          <span className="w-[7px] h-[7px] rounded-full bg-primary/10" />
          <span className="text-[10px] text-primary/40 ml-2 select-none tracking-wide">lignedepresse.com</span>
        </div>

        {/* Workflow steps */}
        <div className="px-4 h-[68px] flex items-center gap-3">
          {steps.map((step, i) => {
            const isActive = i === activeStep;
            const isDone = (activeStep > i) || (activeStep === 0 && i === steps.length - 1 && progress < 10) ? false : activeStep > i;
            return (
              <div key={i} className="flex items-center gap-2">
                <div className="flex flex-col items-center gap-1.5">
                  <div className={`w-7 h-7 rounded-full border flex items-center justify-center transition-all duration-500 ${
                    isActive 
                      ? 'border-primary/40 bg-primary/10 scale-110' 
                      : 'border-border/40 bg-transparent scale-100'
                  }`}>
                    <StepIcon type={step.icon} active={isActive} />
                  </div>
                  <span className={`text-[9px] select-none transition-colors duration-500 whitespace-nowrap ${
                    isActive ? 'text-primary/60' : 'text-muted-foreground/30'
                  }`}>
                    {step.label}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div className="w-6 h-[1px] bg-border/30 mb-4" />
                )}
              </div>
            );
          })}
        </div>

        {/* Progress bar */}
        <div className="h-[2px] bg-primary/5 mx-4 mb-2 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary/30 rounded-full transition-[width] duration-100 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default IllustrationWorkflow;
